import { Form, Formik } from 'formik';
import { number, object } from 'yup';
import { TextField } from '../../common/InputField';
import Button from '../../common/Button';
import { TAdminConfiguration } from '@answerr/types';
import ConfigurationsService from '../../../services/configurations.service';
import { useEffect, useState, useCallback } from 'react';
import { ToastType } from '../../../types/common.types';
import { useAppContext } from '../../../utils/context/app-context';
import Tabs from '../../common/Tabs';
import AiTaskModels from './AiTaskModels';

enum ConfigTab {
  LIMITS = 'limits',
  AIQ = 'aiq',
  MODELS = 'models',
}

const CONFIG_TABS = [
  { value: ConfigTab.LIMITS, label: 'Limits' },
  { value: ConfigTab.AIQ, label: 'AIQ Scoring' },
  { value: ConfigTab.MODELS, label: 'AI Models' },
];

const validationSchema = object().shape({
  freeLimits: object().shape({
    freeCompares: number().required('Free Compares is required').default(3),
    freeImageGeneration: number()
      .required('Free Image Generation is required')
      .default(3),
    freeFileAnalysis: number()
      .required('Free File Analysis is required')
      .default(3),
    freeQueries: number().required('Free Queries is required').default(3),
  }),
  userFileLimits: object().shape({
    maxFiles: number().required('Max Files is required').default(3),
    maxLimit: number().required('Max Limit is required').default(20),
    perFileLimit: number().required('Per File Limit is required').default(10),
  }),
  aiqConfiguration: object().shape({
    signalWeights: object().shape({
      challenge: number()
        .required('Challenge Weight is required')
        .default(0.25),
      verification: number()
        .required('Verification Weight is required')
        .default(0.25),
      chain: number().required('Chain Weight is required').default(0.25),
      recovery: number().required('Recovery Weight is required').default(0.15),
      steering: number().required('Steering Weight is required').default(0.2),
    }),
    dimensionWeights: object().shape({
      jq: number().required('JQ Weight is required').default(0.25),
      qo: number().required('QO Weight is required').default(0.2),
      dt: number().required('DT Weight is required').default(0.2),
      sa: number().required('SA Weight is required').default(0.2),
      lv: number().required('LV Weight is required').default(0.15),
    }),
  }),
});

const DEFAULT_CONFIGURATION: TAdminConfiguration = {
  _id: '',
  freeLimits: {
    freeCompares: 3,
    freeFileAnalysis: 3,
    freeImageGeneration: 3,
    freeQueries: 3,
  },
  userFileLimits: {
    maxFiles: 0,
    maxLimit: 0,
    perFileLimit: 0,
  },
  aiqConfiguration: {
    signalWeights: {
      challenge: 0.25,
      verification: 0.25,
      chain: 0.25,
      recovery: 0.15,
      steering: 0.2,
    },
    dimensionWeights: {
      jq: 0.25,
      qo: 0.2,
      dt: 0.2,
      sa: 0.2,
      lv: 0.15,
    },
  },
  connectors: [],
  updatedAt: new Date(),
};

const normalizeConfiguration = (
  config?: TAdminConfiguration | null
): TAdminConfiguration => ({
  ...DEFAULT_CONFIGURATION,
  ...config,
  freeLimits: {
    freeCompares:
      config?.freeLimits?.freeCompares ??
      DEFAULT_CONFIGURATION.freeLimits?.freeCompares ??
      3,
    freeFileAnalysis:
      config?.freeLimits?.freeFileAnalysis ??
      DEFAULT_CONFIGURATION.freeLimits?.freeFileAnalysis ??
      3,
    freeImageGeneration:
      config?.freeLimits?.freeImageGeneration ??
      DEFAULT_CONFIGURATION.freeLimits?.freeImageGeneration ??
      3,
    freeQueries:
      config?.freeLimits?.freeQueries ??
      DEFAULT_CONFIGURATION.freeLimits?.freeQueries ??
      3,
  },
  userFileLimits: {
    maxFiles:
      config?.userFileLimits?.maxFiles ??
      DEFAULT_CONFIGURATION.userFileLimits?.maxFiles ??
      0,
    maxLimit:
      config?.userFileLimits?.maxLimit ??
      DEFAULT_CONFIGURATION.userFileLimits?.maxLimit ??
      0,
    perFileLimit:
      config?.userFileLimits?.perFileLimit ??
      DEFAULT_CONFIGURATION.userFileLimits?.perFileLimit ??
      0,
  },
  aiqConfiguration: {
    signalWeights: {
      challenge:
        config?.aiqConfiguration?.signalWeights?.challenge ??
        DEFAULT_CONFIGURATION.aiqConfiguration?.signalWeights?.challenge ??
        0.25,
      verification:
        config?.aiqConfiguration?.signalWeights?.verification ??
        DEFAULT_CONFIGURATION.aiqConfiguration?.signalWeights?.verification ??
        0.25,
      chain:
        config?.aiqConfiguration?.signalWeights?.chain ??
        DEFAULT_CONFIGURATION.aiqConfiguration?.signalWeights?.chain ??
        0.25,
      recovery:
        config?.aiqConfiguration?.signalWeights?.recovery ??
        DEFAULT_CONFIGURATION.aiqConfiguration?.signalWeights?.recovery ??
        0.15,
      steering:
        config?.aiqConfiguration?.signalWeights?.steering ??
        DEFAULT_CONFIGURATION.aiqConfiguration?.signalWeights?.steering ??
        0.2,
    },
    dimensionWeights: {
      jq:
        config?.aiqConfiguration?.dimensionWeights?.jq ??
        DEFAULT_CONFIGURATION.aiqConfiguration?.dimensionWeights?.jq ??
        0.25,
      qo:
        config?.aiqConfiguration?.dimensionWeights?.qo ??
        DEFAULT_CONFIGURATION.aiqConfiguration?.dimensionWeights?.qo ??
        0.2,
      dt:
        config?.aiqConfiguration?.dimensionWeights?.dt ??
        DEFAULT_CONFIGURATION.aiqConfiguration?.dimensionWeights?.dt ??
        0.2,
      sa:
        config?.aiqConfiguration?.dimensionWeights?.sa ??
        DEFAULT_CONFIGURATION.aiqConfiguration?.dimensionWeights?.sa ??
        0.2,
      lv:
        config?.aiqConfiguration?.dimensionWeights?.lv ??
        DEFAULT_CONFIGURATION.aiqConfiguration?.dimensionWeights?.lv ??
        0.15,
    },
  },
});

const Configurations = () => {
  const { setToastInfo } = useAppContext();
  const [activeTab, setActiveTab] = useState<ConfigTab>(ConfigTab.LIMITS);
  const [initialValues, setInitialValues] = useState<TAdminConfiguration>(
    DEFAULT_CONFIGURATION
  );

  const getConfiguration = useCallback(async () => {
    try {
      const response = await ConfigurationsService.getConfigurations();
      if (response.data.data?.configuration) {
        setInitialValues(
          normalizeConfiguration(response.data.data?.configuration)
        );
      } else {
        setToastInfo({
          isOpen: true,
          type: ToastType.ERROR,
          message: response.data.message,
        });
      }
    } catch (err) {
      setToastInfo({
        isOpen: true,
        type: ToastType.ERROR,
        message: 'Something went wrong, please try again later.',
      });
    }
  }, [setToastInfo]);

  useEffect(() => {
    getConfiguration();
  }, [getConfiguration]);

  return (
    <div className="flex flex-col flex-grow">
      <Tabs
        items={CONFIG_TABS}
        active={activeTab}
        onChange={(value) => setActiveTab(value as ConfigTab)}
        className="mb-6"
      />

      {activeTab === ConfigTab.MODELS && <AiTaskModels />}

      <div className={activeTab === ConfigTab.MODELS ? 'hidden' : ''}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          validateOnChange={false}
          validateOnBlur={true}
          enableReinitialize={true}
          onSubmit={async (values, { resetForm }) => {
            try {
              const response =
                await ConfigurationsService.updateConfigurations(values);
              if (response.data.data?.configuration) {
                setInitialValues(
                  normalizeConfiguration(response.data.data?.configuration)
                );
                setToastInfo({
                  isOpen: true,
                  type: ToastType.SUCCESS,
                  message: response.data.message,
                });
              } else {
                setToastInfo({
                  isOpen: true,
                  type: ToastType.ERROR,
                  message: response.data.message,
                });
              }
            } catch (err) {
              setToastInfo({
                isOpen: true,
                type: ToastType.ERROR,
                message: 'Something went wrong, please try again later.',
              });
            }
          }}
        >
          {({
            errors,
            touched,
            handleChange,
            handleSubmit,
            handleBlur,
            isValid,
            values,
            dirty,
          }) => {
            return (
              <Form>
                <div
                  className={activeTab === ConfigTab.LIMITS ? 'mb-4' : 'hidden'}
                >
                  <h3 className="text-lg font-semibold mb-4">
                    Free Tier Limits
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="mb-2">
                      <TextField
                        name="freeLimits.freeQueries"
                        label="Free Queries"
                        placeholder=""
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          errors.freeLimits &&
                          typeof errors.freeLimits === 'object'
                            ? (errors.freeLimits as any).freeQueries
                            : undefined
                        }
                        required={true}
                        isTouched={
                          touched.freeLimits &&
                          typeof touched.freeLimits === 'object'
                            ? (touched.freeLimits as any).freeQueries
                            : undefined
                        }
                        value={values.freeLimits?.freeQueries}
                        type="number"
                      />
                    </div>
                    <div className="mb-2">
                      <TextField
                        name="freeLimits.freeCompares"
                        label="Free Compares"
                        placeholder=""
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          errors.freeLimits &&
                          typeof errors.freeLimits === 'object'
                            ? (errors.freeLimits as any).freeCompares
                            : undefined
                        }
                        required={true}
                        isTouched={
                          touched.freeLimits &&
                          typeof touched.freeLimits === 'object'
                            ? (touched.freeLimits as any).freeCompares
                            : undefined
                        }
                        value={values.freeLimits?.freeCompares}
                        type="number"
                      />
                    </div>

                    <div className="mb-2 relative">
                      <TextField
                        name="freeLimits.freeFileAnalysis"
                        label="Free File Analysis"
                        placeholder=""
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          errors.freeLimits &&
                          typeof errors.freeLimits === 'object'
                            ? (errors.freeLimits as any).freeFileAnalysis
                            : undefined
                        }
                        isTouched={
                          touched.freeLimits &&
                          typeof touched.freeLimits === 'object'
                            ? (touched.freeLimits as any).freeFileAnalysis
                            : undefined
                        }
                        value={values.freeLimits?.freeFileAnalysis}
                        required={true}
                        type="number"
                      />
                    </div>

                    <div className="mb-2 relative">
                      <TextField
                        name="freeLimits.freeImageGeneration"
                        label="Free Image Generations"
                        placeholder=""
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          errors.freeLimits &&
                          typeof errors.freeLimits === 'object'
                            ? (errors.freeLimits as any).freeImageGeneration
                            : undefined
                        }
                        isTouched={
                          touched.freeLimits &&
                          typeof touched.freeLimits === 'object'
                            ? (touched.freeLimits as any).freeImageGeneration
                            : undefined
                        }
                        value={values.freeLimits?.freeImageGeneration}
                        required={true}
                        type="number"
                      />
                    </div>
                  </div>
                </div>

                <div
                  className={activeTab === ConfigTab.LIMITS ? 'mb-4' : 'hidden'}
                >
                  <h3 className="text-lg font-semibold mb-4">
                    User File Limits
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="mb-2 relative">
                      <TextField
                        name="userFileLimits.maxFiles"
                        label="Max Files"
                        placeholder=""
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          errors.userFileLimits &&
                          typeof errors.userFileLimits === 'object'
                            ? (errors.userFileLimits as any).maxFiles
                            : undefined
                        }
                        isTouched={
                          touched.userFileLimits &&
                          typeof touched.userFileLimits === 'object'
                            ? (touched.userFileLimits as any).maxFiles
                            : undefined
                        }
                        value={values.userFileLimits?.maxFiles}
                        required={true}
                        type="number"
                      />
                    </div>

                    <div className="mb-2 relative">
                      <TextField
                        name="userFileLimits.maxLimit"
                        label="Max Limit (MB)"
                        placeholder=""
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          errors.userFileLimits &&
                          typeof errors.userFileLimits === 'object'
                            ? (errors.userFileLimits as any).maxLimit
                            : undefined
                        }
                        isTouched={
                          touched.userFileLimits &&
                          typeof touched.userFileLimits === 'object'
                            ? (touched.userFileLimits as any).maxLimit
                            : undefined
                        }
                        value={values.userFileLimits?.maxLimit}
                        required={true}
                        type="number"
                      />
                    </div>

                    <div className="mb-2 relative">
                      <TextField
                        name="userFileLimits.perFileLimit"
                        label="Per File Limit (MB)"
                        placeholder=""
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          errors.userFileLimits &&
                          typeof errors.userFileLimits === 'object'
                            ? (errors.userFileLimits as any).perFileLimit
                            : undefined
                        }
                        isTouched={
                          touched.userFileLimits &&
                          typeof touched.userFileLimits === 'object'
                            ? (touched.userFileLimits as any).perFileLimit
                            : undefined
                        }
                        value={values.userFileLimits?.perFileLimit}
                        required={true}
                        type="number"
                      />
                    </div>
                  </div>
                </div>

                <div
                  className={activeTab === ConfigTab.AIQ ? 'mb-4' : 'hidden'}
                >
                  <h3 className="text-lg font-semibold mb-4">
                    AIQ Signal Weights
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="mb-2 relative">
                      <TextField
                        name="aiqConfiguration.signalWeights.challenge"
                        label="Challenge"
                        placeholder=""
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          errors.aiqConfiguration &&
                          typeof errors.aiqConfiguration === 'object'
                            ? (errors.aiqConfiguration as any)?.signalWeights
                                ?.challenge
                            : undefined
                        }
                        isTouched={
                          touched.aiqConfiguration &&
                          typeof touched.aiqConfiguration === 'object'
                            ? (touched.aiqConfiguration as any)?.signalWeights
                                ?.challenge
                            : undefined
                        }
                        value={
                          values.aiqConfiguration?.signalWeights?.challenge
                        }
                        required={true}
                        type="number"
                      />
                    </div>

                    <div className="mb-2 relative">
                      <TextField
                        name="aiqConfiguration.signalWeights.verification"
                        label="Verification"
                        placeholder=""
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          errors.aiqConfiguration &&
                          typeof errors.aiqConfiguration === 'object'
                            ? (errors.aiqConfiguration as any)?.signalWeights
                                ?.verification
                            : undefined
                        }
                        isTouched={
                          touched.aiqConfiguration &&
                          typeof touched.aiqConfiguration === 'object'
                            ? (touched.aiqConfiguration as any)?.signalWeights
                                ?.verification
                            : undefined
                        }
                        value={
                          values.aiqConfiguration?.signalWeights?.verification
                        }
                        required={true}
                        type="number"
                      />
                    </div>

                    <div className="mb-2 relative">
                      <TextField
                        name="aiqConfiguration.signalWeights.chain"
                        label="Chain"
                        placeholder=""
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          errors.aiqConfiguration &&
                          typeof errors.aiqConfiguration === 'object'
                            ? (errors.aiqConfiguration as any)?.signalWeights
                                ?.chain
                            : undefined
                        }
                        isTouched={
                          touched.aiqConfiguration &&
                          typeof touched.aiqConfiguration === 'object'
                            ? (touched.aiqConfiguration as any)?.signalWeights
                                ?.chain
                            : undefined
                        }
                        value={values.aiqConfiguration?.signalWeights?.chain}
                        required={true}
                        type="number"
                      />
                    </div>

                    <div className="mb-2 relative">
                      <TextField
                        name="aiqConfiguration.signalWeights.recovery"
                        label="Recovery"
                        placeholder=""
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          errors.aiqConfiguration &&
                          typeof errors.aiqConfiguration === 'object'
                            ? (errors.aiqConfiguration as any)?.signalWeights
                                ?.recovery
                            : undefined
                        }
                        isTouched={
                          touched.aiqConfiguration &&
                          typeof touched.aiqConfiguration === 'object'
                            ? (touched.aiqConfiguration as any)?.signalWeights
                                ?.recovery
                            : undefined
                        }
                        value={values.aiqConfiguration?.signalWeights?.recovery}
                        required={true}
                        type="number"
                      />
                    </div>

                    <div className="mb-2 relative">
                      <TextField
                        name="aiqConfiguration.signalWeights.steering"
                        label="Steering"
                        placeholder=""
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          errors.aiqConfiguration &&
                          typeof errors.aiqConfiguration === 'object'
                            ? (errors.aiqConfiguration as any)?.signalWeights
                                ?.steering
                            : undefined
                        }
                        isTouched={
                          touched.aiqConfiguration &&
                          typeof touched.aiqConfiguration === 'object'
                            ? (touched.aiqConfiguration as any)?.signalWeights
                                ?.steering
                            : undefined
                        }
                        value={values.aiqConfiguration?.signalWeights?.steering}
                        required={true}
                        type="number"
                      />
                    </div>
                  </div>
                </div>

                <div
                  className={activeTab === ConfigTab.AIQ ? 'mb-4' : 'hidden'}
                >
                  <h3 className="text-lg font-semibold mb-4">
                    AIQ Dimension Weights
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="mb-2 relative">
                      <TextField
                        name="aiqConfiguration.dimensionWeights.jq"
                        label="JQ"
                        placeholder=""
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          errors.aiqConfiguration &&
                          typeof errors.aiqConfiguration === 'object'
                            ? (errors.aiqConfiguration as any)?.dimensionWeights
                                ?.jq
                            : undefined
                        }
                        isTouched={
                          touched.aiqConfiguration &&
                          typeof touched.aiqConfiguration === 'object'
                            ? (touched.aiqConfiguration as any)
                                ?.dimensionWeights?.jq
                            : undefined
                        }
                        value={values.aiqConfiguration?.dimensionWeights?.jq}
                        required={true}
                        type="number"
                      />
                    </div>

                    <div className="mb-2 relative">
                      <TextField
                        name="aiqConfiguration.dimensionWeights.qo"
                        label="QO"
                        placeholder=""
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          errors.aiqConfiguration &&
                          typeof errors.aiqConfiguration === 'object'
                            ? (errors.aiqConfiguration as any)?.dimensionWeights
                                ?.qo
                            : undefined
                        }
                        isTouched={
                          touched.aiqConfiguration &&
                          typeof touched.aiqConfiguration === 'object'
                            ? (touched.aiqConfiguration as any)
                                ?.dimensionWeights?.qo
                            : undefined
                        }
                        value={values.aiqConfiguration?.dimensionWeights?.qo}
                        required={true}
                        type="number"
                      />
                    </div>

                    <div className="mb-2 relative">
                      <TextField
                        name="aiqConfiguration.dimensionWeights.dt"
                        label="DT"
                        placeholder=""
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          errors.aiqConfiguration &&
                          typeof errors.aiqConfiguration === 'object'
                            ? (errors.aiqConfiguration as any)?.dimensionWeights
                                ?.dt
                            : undefined
                        }
                        isTouched={
                          touched.aiqConfiguration &&
                          typeof touched.aiqConfiguration === 'object'
                            ? (touched.aiqConfiguration as any)
                                ?.dimensionWeights?.dt
                            : undefined
                        }
                        value={values.aiqConfiguration?.dimensionWeights?.dt}
                        required={true}
                        type="number"
                      />
                    </div>

                    <div className="mb-2 relative">
                      <TextField
                        name="aiqConfiguration.dimensionWeights.sa"
                        label="SA"
                        placeholder=""
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          errors.aiqConfiguration &&
                          typeof errors.aiqConfiguration === 'object'
                            ? (errors.aiqConfiguration as any)?.dimensionWeights
                                ?.sa
                            : undefined
                        }
                        isTouched={
                          touched.aiqConfiguration &&
                          typeof touched.aiqConfiguration === 'object'
                            ? (touched.aiqConfiguration as any)
                                ?.dimensionWeights?.sa
                            : undefined
                        }
                        value={values.aiqConfiguration?.dimensionWeights?.sa}
                        required={true}
                        type="number"
                      />
                    </div>

                    <div className="mb-2 relative">
                      <TextField
                        name="aiqConfiguration.dimensionWeights.lv"
                        label="LV"
                        placeholder=""
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          errors.aiqConfiguration &&
                          typeof errors.aiqConfiguration === 'object'
                            ? (errors.aiqConfiguration as any)?.dimensionWeights
                                ?.lv
                            : undefined
                        }
                        isTouched={
                          touched.aiqConfiguration &&
                          typeof touched.aiqConfiguration === 'object'
                            ? (touched.aiqConfiguration as any)
                                ?.dimensionWeights?.lv
                            : undefined
                        }
                        value={values.aiqConfiguration?.dimensionWeights?.lv}
                        required={true}
                        type="number"
                      />
                    </div>
                  </div>
                </div>

                <div className="text-center pb-5">
                  <Button
                    type="button"
                    disabled={!touched || !isValid || !dirty}
                    onClick={() => handleSubmit()}
                  >
                    Update Configurations
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  );
};

export default Configurations;
